/**
 * The legal documents: the ÁSZF and the privacy notice, Hungarian and English
 * (legal pages brief, 13 October 2026).
 *
 * The text is Barna's, in `docs/legal/PLT_catering_<name>_<lang>_<YYYYMMDD>.md`,
 * and nothing here writes a word of it. This module knows only where each
 * document lives, which version is current, and how to turn the small amount
 * of Markdown the files use into blocks — so that the static pages
 * (`apps/web/legalPages.ts`), the ÁSZF PDF (`scripts/legal_pdf.ts`) and the
 * links in U1 and U5 all read the same file the same way.
 *
 * The version is the date in the file name. It is also the register's
 * `legal_terms_version` / `legal_privacy_version`, which `orders` records at
 * submission; `supabase/tests/420_legal_versions.test.sql` holds the two
 * equal.
 *
 * Pure: no file system, no clock, no environment. Deno imports it through
 * `supabase/functions/_shared/emails.ts` as well as Node and the browser.
 */

export type LegalDoc = 'aszf' | 'privacy'
export type LegalLang = 'hu' | 'en'

/** The version in force of each document, as `YYYYMMDD`. */
export const LEGAL_VERSIONS: Readonly<Record<LegalDoc, string>> = {
  aszf: '20261013',
  privacy: '20261013',
}

interface DocLang {
  /** The path on the customer site. */
  path: string
  /** The source file's name without its `_YYYYMMDD.md`. */
  file: string
  /** The name a link, a footer or a checkbox uses. */
  label: string
}

export const LEGAL_DOCS: Readonly<Record<LegalDoc, Readonly<Record<LegalLang, DocLang>>>> = {
  aszf: {
    hu: { path: '/aszf', file: 'PLT_catering_aszf_hu', label: 'ÁSZF' },
    en: { path: '/en/terms', file: 'PLT_catering_terms_en', label: 'Terms and Conditions' },
  },
  privacy: {
    hu: {
      path: '/adatkezeles',
      file: 'PLT_catering_adatkezelesi_tajekoztato_hu',
      label: 'Adatkezelési tájékoztató',
    },
    en: { path: '/en/privacy', file: 'PLT_catering_privacy_notice_en', label: 'Privacy Notice' },
  },
}

export const LEGAL_DOC_KEYS: readonly LegalDoc[] = ['aszf', 'privacy']
export const LEGAL_LANGS: readonly LegalLang[] = ['hu', 'en']

export const legalLang = (lang: string | null | undefined): LegalLang => (lang === 'en' ? 'en' : 'hu')

/** `docs/legal/…` file name of one document in one version. */
export const legalFileName = (doc: LegalDoc, lang: LegalLang, version = LEGAL_VERSIONS[doc]): string =>
  `${LEGAL_DOCS[doc][lang].file}_${version}.md`

/** The page's address on a site, or the bare path when there is no site. */
export const legalUrl = (doc: LegalDoc, lang: LegalLang, siteUrl?: string | null): string =>
  `${(siteUrl ?? '').replace(/\/+$/, '')}${LEGAL_DOCS[doc][lang].path}`

/** The ÁSZF PDF's file name as the customer sees it on U5. */
export const legalPdfName = (lang: LegalLang, version: string): string =>
  lang === 'en' ? `Planteen_Terms_and_Conditions_${version}.pdf` : `Planteen_ASZF_${version}.pdf`

/* ------------------------------------------------------------------ *
 * The Markdown the files use, and no more
 * ------------------------------------------------------------------ */

/** A run of text, bold or not. `**…**` is the only inline mark in the files. */
export interface Span {
  text: string
  bold: boolean
}

export type LegalBlock =
  | { kind: 'h1'; spans: Span[] }
  | { kind: 'h2'; spans: Span[] }
  | { kind: 'p'; spans: Span[] }
  | { kind: 'ul'; items: Span[][] }
  | { kind: 'table'; head: Span[][]; rows: Span[][][] }

export function parseInline(text: string): Span[] {
  const spans: Span[] = []
  const parts = text.split('**')
  // An odd number of `**` would leave the last part open; it is then text.
  const balanced = parts.length % 2 === 1
  parts.forEach((part, index) => {
    if (part === '') return
    const bold = balanced && index % 2 === 1
    spans.push({ text: balanced ? part : index === 0 ? part : `**${part}`, bold })
  })
  return spans
}

const tableCells = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())

const isSeparatorRow = (line: string): boolean => /^\|?(\s*:?-+:?\s*\|)+\s*:?-*:?\s*\|?$/.test(line.trim())

/**
 * Headings (`#`, `##`), paragraphs, `- ` lists, pipe tables and `**bold**`.
 * Anything else stays text: a construct the parser does not know is shown as
 * written rather than dropped, so nothing in a source file can go missing.
 */
export function parseLegalMarkdown(source: string): LegalBlock[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: LegalBlock[] = []
  let index = 0
  while (index < lines.length) {
    const line = lines[index]!
    const trimmed = line.trim()
    if (trimmed === '') {
      index += 1
      continue
    }
    if (trimmed.startsWith('## ')) {
      blocks.push({ kind: 'h2', spans: parseInline(trimmed.slice(3).trim()) })
      index += 1
      continue
    }
    if (trimmed.startsWith('# ')) {
      blocks.push({ kind: 'h1', spans: parseInline(trimmed.slice(2).trim()) })
      index += 1
      continue
    }
    if (trimmed.startsWith('- ')) {
      const items: Span[][] = []
      while (index < lines.length && lines[index]!.trim().startsWith('- ')) {
        items.push(parseInline(lines[index]!.trim().slice(2).trim()))
        index += 1
      }
      blocks.push({ kind: 'ul', items })
      continue
    }
    if (trimmed.startsWith('|') && index + 1 < lines.length && isSeparatorRow(lines[index + 1]!)) {
      const head = tableCells(trimmed).map(parseInline)
      index += 2
      const rows: Span[][][] = []
      while (index < lines.length && lines[index]!.trim().startsWith('|')) {
        rows.push(tableCells(lines[index]!).map(parseInline))
        index += 1
      }
      blocks.push({ kind: 'table', head, rows })
      continue
    }
    const paragraph: string[] = []
    while (index < lines.length) {
      const next = lines[index]!.trim()
      if (next === '' || next.startsWith('#') || next.startsWith('- ') || next.startsWith('|')) break
      paragraph.push(next)
      index += 1
    }
    blocks.push({ kind: 'p', spans: parseInline(paragraph.join(' ')) })
  }
  return blocks
}

/* ------------------------------------------------------------------ *
 * HTML
 * ------------------------------------------------------------------ */

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const spansHtml = (spans: Span[]): string =>
  spans.map((span) => (span.bold ? `<strong>${escapeHtml(span.text)}</strong>` : escapeHtml(span.text))).join('')

export function legalBlocksHtml(blocks: LegalBlock[]): string {
  return blocks
    .map((block) => {
      switch (block.kind) {
        case 'h1':
          return `<h1>${spansHtml(block.spans)}</h1>`
        case 'h2':
          return `<h2>${spansHtml(block.spans)}</h2>`
        case 'p':
          return `<p>${spansHtml(block.spans)}</p>`
        case 'ul':
          return `<ul>${block.items.map((item) => `<li>${spansHtml(item)}</li>`).join('')}</ul>`
        case 'table':
          return (
            `<div class="legal-table"><table><thead><tr>` +
            block.head.map((cell) => `<th scope="col">${spansHtml(cell)}</th>`).join('') +
            `</tr></thead><tbody>` +
            block.rows.map((row) => `<tr>${row.map((cell) => `<td>${spansHtml(cell)}</td>`).join('')}</tr>`).join('') +
            `</tbody></table></div>`
          )
      }
    })
    .join('\n')
}

/* ------------------------------------------------------------------ *
 * Words, for the check that a rendering says what the file says
 * ------------------------------------------------------------------ */

/** The source's words in order, with the Markdown marks taken out. */
export function sourceWords(source: string): string[] {
  return source
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => !isSeparatorRow(line))
    .map((line) =>
      line
        .replace(/^\s*#{1,2}\s+/, '')
        .replace(/^\s*-\s+/, '')
        .replace(/\*\*/g, '')
        .replace(/\|/g, ' '),
    )
    .join(' ')
    .split(/\s+/)
    .filter((word) => word !== '')
}

/** The words of a run of blocks, in reading order. */
export function blockWords(blocks: LegalBlock[]): string[] {
  const text: string[] = []
  const spans = (list: Span[]) => text.push(list.map((span) => span.text).join(''))
  for (const block of blocks) {
    if (block.kind === 'ul') block.items.forEach(spans)
    else if (block.kind === 'table') {
      block.head.forEach(spans)
      block.rows.forEach((row) => row.forEach(spans))
    } else spans(block.spans)
  }
  return text
    .join(' ')
    .split(/\s+/)
    .filter((word) => word !== '')
}
